import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Text } from "@clipboard-health/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import { type UseModalState } from "@clipboard-health/ui-react";
import {
  formatShortDateWithWeekday,
  formatTime,
  formatTimeZone,
} from "@clipboard-health/date-time";

import { logError, logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { showErrorToast, showInfoToast } from "@src/appV2/lib/Notifications";
import { useSendWorkerCancelAlert } from "@src/appV2/api/slack/useSendWorkerCancelAlert";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { type Shift } from "@src/interface";
import { getWorkerShiftTypeLabel } from "@src/appV2/redesign/utils/worker";
import { QuestionCircleOutlined } from "@ant-design/icons";

interface WorkerRequestedCancelModalProps {
  modalState: UseModalState;
  // When shift is available, all data is extracted from it
  shift?: Pick<Shift, "_id" | "agent" | "agentId" | "start" | "facility" | "agentReq" | "end">;
  // Fallbacks for when shift is not available (e.g., chat context)
  workerId?: string;
  workerName?: string;
  facilityId?: string;
  workerQualification?: string;
}

export function WorkerRequestedCancelModal(props: WorkerRequestedCancelModalProps) {
  const {
    modalState,
    shift,
    workerId: workerIdProp,
    workerName: workerNameProp,
    facilityId: facilityIdProp,
    workerQualification: workerQualificationProp,
  } = props;

  const { mutateAsync: sendWorkerCancelAlert, isLoading } = useSendWorkerCancelAlert();
  const { user } = useSession();
  const [shouldReplaceWorker, setShouldReplaceWorker] = useState(true);
  const [step, setStep] = useState<"report" | "confirm">("report");

  // Derive values from shift or fallback to props
  const shiftId = shift?._id;
  const workerId = shift?.agentId ?? workerIdProp;
  const workerName = shift?.agent?.name ?? workerNameProp;
  const facilityId = shift?.facility?.userId ?? facilityIdProp;
  const logModalEvent = (action: string, extra?: Record<string, unknown>) => {
    logEvent(APP_V2_USER_EVENTS.WORKER_CANCEL_REPORT_MODAL_ACTIONED, {
      action,
      workplaceId: facilityId,
      workplaceUserId: user?._id,
      workerId,
      shiftId,
      ...extra,
    });
  };

  const isFilledShift = isDefined(shift?.start) && isDefined(shift?.agent);
  const qualificationLabel =
    shift?.agentReq && isFilledShift
      ? getWorkerShiftTypeLabel(shift.agentReq, shift.facility?.fullAddress?.state)
      : workerQualificationProp;

  const timezone = shift?.facility?.tmz ?? "UTC";
  const workerFirstName = workerName?.split(" ")[0];

  const handleClose = () => {
    logModalEvent("modal_dismissed", { step });
    modalState.closeModal();
  };

  const handleExited = () => {
    setStep("report");
  };

  const handleConfirm = async () => {
    if (!workerId || !workerName) {
      return;
    }

    try {
      await sendWorkerCancelAlert({
        shiftId,
        workerId,
        facilityId,
        shouldReplaceWorker,
      });
      logModalEvent("report_submitted", { shouldReplaceWorker });
      const message = shouldReplaceWorker
        ? "Your report has been submitted. We'll review it and find a replacement worker shortly."
        : "Your report has been submitted. Our team will review it and get back to you shortly.";
      showInfoToast(message);
    } catch (error) {
      logError("Failed to send worker cancel alert", { error });
      showErrorToast("Unable to send notification. Please contact support.");
    } finally {
      handleClose();
    }
  };

  return (
    <Dialog
      maxWidth={false}
      open={modalState.modalIsOpen}
      onClose={handleClose}
      TransitionProps={{
        onEntered: () => logModalEvent("modal_opened"),
        onExited: handleExited,
      }}
      PaperProps={{ sx: { width: 480 } }}
    >
      {step === "report" && (
        <>
          <DialogTitle
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
          >
            <QuestionCircleOutlined
              style={{ color: "#faad14", fontSize: "22px", marginRight: "16px" }}
            />
            <span>Report professional asking you to cancel</span>
            <IconButton aria-label="close" onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stack
              spacing={2}
              sx={(theme) => ({
                backgroundColor: theme.palette.grey[100],
                borderRadius: 2,
                padding: 2,
              })}
            >
              {workerName && (
                <Text variant="body1" bold>
                  {workerName}
                  {qualificationLabel && `, ${qualificationLabel}`}
                </Text>
              )}

              {shift && (
                <Box>
                  <Text variant="body2" bold>
                    {formatShortDateWithWeekday(shift.start, { timeZone: timezone })}
                  </Text>
                  <Text variant="body2" color="text.secondary">
                    {formatTime(shift.start, { timeZone: timezone })} to{" "}
                    {formatTime(shift.end, { timeZone: timezone })}{" "}
                    {formatTimeZone(shift.start, { timeZone: timezone })}
                  </Text>
                </Box>
              )}
              <RadioGroup
                value={shouldReplaceWorker}
                onChange={(e) => {
                  const needsReplacement = e.target.value === "true";
                  setShouldReplaceWorker(needsReplacement);
                  logModalEvent(
                    needsReplacement
                      ? "replacement_option_selected"
                      : "no_replacement_option_selected"
                  );
                }}
                sx={{ mt: 2 }}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="I need a replacement for this shift"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="I no longer need this shift."
                />
              </RadioGroup>
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: "space-between",
              paddingLeft: 3,
              paddingRight: 3,
              paddingBottom: 2,
              paddingTop: 0,
            }}
          >
            <Button variant="outlined" onClick={handleClose}>
              Nevermind
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setStep("confirm");
                logModalEvent("confirmation_step_reached", { shouldReplaceWorker });
              }}
            >
              Continue
            </Button>
          </DialogActions>
        </>
      )}

      {step === "confirm" && (
        <>
          <DialogTitle
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
          >
            <span>Are you sure?</span>
            <IconButton aria-label="close" onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Text variant="body1">
              By confirming, you are reporting a potential policy violation. Our team will review
              this report and take necessary action, which includes{" "}
              <Box component="span" sx={{ color: "error.main" }}>
                removing {workerFirstName ?? "the professional"} from the shift, and if applicable
                applying penalties to their account
              </Box>
              .
            </Text>
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: "space-between",
              paddingLeft: 3,
              paddingRight: 3,
              paddingBottom: 2,
              paddingTop: 0,
            }}
          >
            <Button variant="outlined" onClick={handleClose} disabled={isLoading}>
              Nevermind
            </Button>
            <Button variant="contained" onClick={handleConfirm} disabled={isLoading}>
              Confirm
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
